import { Building2, HardHat, Layers } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

export type DashboardBusinessMode = "property" | "services";

export type DashboardHeaderProps = {
  mode: DashboardBusinessMode;
  onModeChange: (mode: DashboardBusinessMode) => void;
  hideToggle?: boolean;
  hideFinancials?: boolean;
  isHybrid?: boolean;
};

export function DashboardHeader({ mode, onModeChange, hideToggle, hideFinancials, isHybrid }: DashboardHeaderProps) {
  const subtitle = hideFinancials
    ? "Overview of your operations."
    : isHybrid
      ? "Hybrid org — bookings and services in one view. Toggle to focus."
      : mode === "property"
        ? "Overview of your property portfolio."
        : "Overview of your cleaning & services operations.";

  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <h1
            className="text-2xl sm:text-3xl font-display font-bold"
            data-testid="text-dashboard-title"
          >
            Dashboard
          </h1>
          {isHybrid && (
            <Badge variant="outline" className="gap-1 text-[10px] uppercase tracking-wider border-primary/40 text-primary">
              <Layers className="h-3 w-3" /> Hybrid
            </Badge>
          )}
        </div>
        <p className="text-muted-foreground mt-2" data-testid="text-dashboard-subtitle">
          {subtitle}
        </p>
      </div>

      {!hideToggle && (
        <div
          className="flex rounded-xl bg-muted p-1 gap-1 w-full sm:w-auto"
          data-testid="toggle-dashboard-mode"
        >
          <ModeButton
            label="Property Mgmt"
            icon={<Building2 className="w-4 h-4 shrink-0" />}
            active={mode === "property"}
            onClick={() => onModeChange("property")}
            testId="btn-dashboard-property"
          />
          <ModeButton
            label="Services"
            icon={<HardHat className="w-4 h-4 shrink-0" />}
            active={mode === "services"}
            onClick={() => onModeChange("services")}
            testId="btn-dashboard-services"
          />
        </div>
      )}
    </div>
  );
}

type ModeButtonProps = {
  label: string;
  icon: React.ReactNode;
  active: boolean;
  onClick: () => void;
  testId: string;
};

function ModeButton({ label, icon, active, onClick, testId }: ModeButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={cn(
        "flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 sm:py-2.5",
        "rounded-lg text-xs sm:text-sm font-medium transition-all flex-1 sm:flex-initial min-w-0",
        active
          ? "bg-background text-foreground shadow-sm"
          : "text-muted-foreground hover:text-foreground",
      )}
    >
      {icon}
      <span className="truncate">{label}</span>
    </button>
  );
}

export default DashboardHeader;
